/**
 * Claims management utilities
 * Extracted from FundingKit for reuse across persona-aware contexts
 */

/**
 * Claim status values:
 * -1 = Rejected
 *  0 = Pending verification
 *  1 = Approved/Claimed
 */

/**
 * Filter out rejected claims (status === -1)
 * @param claims - Array of claim objects
 * @returns Claims that are not rejected
 */
export const filterValidClaims = (claims: any[]): any[] => {
  if (!claims || !Array.isArray(claims)) {
    return [];
  }
  return claims.filter((claim) => claim.status !== -1);
};

/**
 * Find the first approved claim (status === 1)
 * @param claims - Array of claim objects
 * @returns First approved claim or undefined
 */
export const findApprovedClaim = (claims: any[]): any | undefined => {
  if (!claims || !Array.isArray(claims)) {
    return undefined;
  }
  return claims.find((claim) => claim.status === 1);
};

/**
 * Check if any claims are pending verification (status === 0)
 * @param claims - Array of claim objects
 * @returns true if any pending claims exist
 */
export const hasPendingClaims = (claims: any[]): boolean => {
  if (!claims || !Array.isArray(claims)) {
    return false;
  }
  return claims.some((claim) => claim.status === 0);
};

/**
 * Check if user has any valid claims (not rejected)
 * @param claims - Array of claim objects
 * @returns true if valid claims exist
 */
export const hasValidClaims = (claims: any[]): boolean => {
  const validClaims = filterValidClaims(claims);
  return validClaims.length > 0;
};

/**
 * Find a specific claim by organization ID
 * @param claims - Array of claim objects
 * @param orgId - Organization ID to find
 * @returns Matching claim or undefined
 */
export const findClaimByOrgId = (claims: any[], orgId: string): any | undefined => {
  if (!claims || !Array.isArray(claims)) {
    return undefined;
  }
  return claims.find((claim) => claim?.organization?.id === orgId);
};

/**
 * Determine which personas can claim organizations
 * @param persona - User persona type
 * @returns true if persona can claim organizations
 */
export const canClaimOrganization = (persona: string): boolean => {
  return ["FOUNDER", "INVESTOR", "ACCELERATOR"].includes(persona);
};
